import React, { createRef, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { MsalContext } from './MsalContext';
import { useSnackbar, VariantType } from 'notistack';
import { createContext, useContext, useState } from 'react';
import ReturnViewModel from './ViewModels/ReturnViewModel';

type ContextModel = {
    isClientAIEnabled: boolean,
    isClientTranscriptsEnabled: boolean;
    isClient: boolean;
    isExec: boolean;
    isAdmin: boolean;
    isQA: boolean;
    isTeamLeader: boolean;
    isQATeamLeader: boolean;
    isAgent: boolean;
    isRTM: boolean;
    isDataDialler: boolean;
    setIsClientAIEnabled: (isClientAIEnabled: boolean) => void;
    setIsClientTranscriptsEnabled: (isClientTranscriptsEnabled: boolean) => void;
    setIsRTM: (isRTM: boolean) => void;
    setIsClient: (isClient: boolean) => void;
    setIsExec: (isClient: boolean) => void;
    setIsAdmin: (isAdmin: boolean) => void;
    setIsQA: (isQA: boolean) => void;
    setIsAgent: (isAgent: boolean) => void;
    isResqAnywhere: () => boolean;
    setIsTeamLeader: (isTeamLeader: boolean) => void;
    setIsQATeamLeader: (isQATeamLeader: boolean) => void;
    setIsDataDialler: (isDataDiller: boolean) => void;
    showMessage: (severity: VariantType, message: string) => void;
    post: (controller: string, action: string, data: any, onSuccess?: (data: any) => void, onFailure?: () => void) => void;
    get: (controller: string, action: string, callback?: (data: any) => void, onFailure?: () => void) => void;
    checkRoles: () => void;
    loading: boolean;
    wavesurfer?: React.MutableRefObject<WaveSurfer | null>;
    waveRef?: React.RefObject<HTMLDivElement>;
};

export const APIContext = createContext<ContextModel>({
    isClientAIEnabled: false,
    isClientTranscriptsEnabled: false,
    isClient: false,
    isExec: false,
    isAdmin: false,
    isQA: false,
    isTeamLeader: false,
    isQATeamLeader: false,
    isAgent: false,
    isRTM: false,
    isDataDialler: false,
    isResqAnywhere: () => { throw new Error('Cannot use context without provider') },
    setIsRTM: () => { throw new Error('Cannot use context without provider') },
    setIsClientAIEnabled: () => { throw new Error('Cannot use context without provider') },
    setIsClientTranscriptsEnabled: () => { throw new Error('Cannot use context without provider') },
    setIsClient: () => { throw new Error('Cannot use context without provider') },
    setIsExec: () => { throw new Error('Cannot use context without provider') },
    setIsTeamLeader: () => { throw new Error('Cannot use context without provider') },
    setIsQATeamLeader: () => { throw new Error('Cannot use context without provider') },
    setIsAdmin: () => { throw new Error('Cannot use context without provider') },
    setIsAgent: () => { throw new Error('Cannot use context without provider') },
    setIsQA: () => { throw new Error('Cannot use context without provider') },
    setIsDataDialler: () => { throw new Error('Cannot use context without provider') },
    showMessage: () => { throw Error('Cannot use a context without a provider'); },
    post: () => { throw Error('Cannot use a context without a provider'); },
    get: () => { throw Error('Cannot use a context without a provider'); },
    checkRoles: () => { throw Error('Cannot use a context without a provider'); },
    loading: false,
    wavesurfer: undefined,
    waveRef: undefined
});

export default function AppProvider({ children }: React.PropsWithChildren<{}>) {
    const { msalFetch } = useContext(MsalContext);
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(false);
    const [isClientAIEnabled, setIsClientAIEnabled] = useState(false);
    const [isClientTranscriptsEnabled, setIsClientTranscriptsEnabled] = useState(false);
    const [isClient, setIsClient] = useState(false);
    const [isRTM, setIsRTM] = useState(false);
    const [isExec, setIsExec] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isQA, setIsQA] = useState(false);
    const [isAgent, setIsAgent] = useState(false);
    const [isTeamLeader, setIsTeamLeader] = useState<boolean>(false);
    const [isQATeamLeader, setIsQATeamLeader] = useState<boolean>(false);
    const [isDataDialler, setIsDataDialler] = useState<boolean>(false);
    let waveRef = createRef<HTMLDivElement>();
    const wavesurfer = useRef<WaveSurfer>(null);

    const isResqAnywhere = () => {
        //admins, Execs, and TLS don't need to use ResQ Anywhere
        return navigator.userAgent.toLowerCase().includes('resqanywhere') || isExec || isAdmin || isTeamLeader;
    }

    const showMessage = (severity: VariantType, message: string) => {
        enqueueSnackbar(message, {
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
            },
            variant: severity
        });
        console.log(severity + ": " + message);
    }

    const checkRoles = () => {
        msalFetch('api/User/IsClient')
            .then(response => response.json())
            .then(data => {
                setIsClient(data);
            });

        msalFetch(`api/User/IsClientAIEnabled`)
            .then(response => response.json()).then(data => {
                setIsClientAIEnabled(data);
            })

        msalFetch('api/User/IsClientTranscriptsEnabled')
            .then(response => response.json())
            .then(data => {
                setIsClientTranscriptsEnabled(data.value);
            });

        msalFetch('api/User/IsRtm')
            .then(response => response.json())
            .then(data => {
                setIsRTM(data);
            });

        msalFetch('api/User/IsQA')
            .then(response => response.json())
            .then(data => {
                setIsQA(data);
            });

        msalFetch('api/User/IsAgent')
            .then(response => response.json())
            .then(data => {
                setIsAgent(data);
            });

        msalFetch('api/User/IsDataDialler')
            .then(response => response.json())
            .then(data => {
                setIsDataDialler(data);
            });

        msalFetch('api/User/IsTeamLeader')
            .then(response => response.json())
            .then(data => {
                setIsTeamLeader(data);
            });

        msalFetch('api/User/IsQATeamLeader')
            .then(response => response.json())
            .then(data => {
                setIsQATeamLeader(data);
            });

        msalFetch('api/User/IsExec')
            .then(response => response.json())
            .then(data => setIsExec(data))

        msalFetch('api/User/IsAdmin')
            .then(response => response.json())
            .then(data => {
                setIsAdmin(data);
            });
    }

    const post = (
        controller: string, action: string, data: any, onSuccess?: (data: any) => void, onFailure?: () => void) => {
        setLoading(true);
        const vm = JSON.stringify(data);
        msalFetch(`api/${controller}/${action}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: vm
        })
            .then(response => response.json())
            .then((data: ReturnViewModel) => {
                setLoading(false);
                if (data.success) {
                    if (onSuccess !== undefined) {
                        onSuccess(data.value);
                    }
                }
                else {
                    console.log(data);
                    for (var i = 0; i < data.messages.length; i++) {
                        showMessage('error', data.messages[i]);
                    }
                    if (onFailure !== undefined) {
                        onFailure();
                    }
                }
            })
            .catch(error => {
                if (onFailure !== undefined) {
                    onFailure();
                }
                setLoading(false);
                console.log(error);
            });
    }

    const get = (controller: string, action: string, callback?: (data: any) => void, onFailure?: () => void) => {
        setLoading(true);
        msalFetch(`api/${controller}/${action}`)
            .then(response => response.json())
            .then((data: ReturnViewModel) => {
                setLoading(false);
                if (data.success) {
                    if (callback !== undefined) {
                        callback(data.value);
                    }
                }
                else {
                    for (var i = 0; i < data.messages.length; i++) {
                        showMessage('error', data.messages[i]);
                    }
                    if (onFailure !== undefined) {
                        onFailure();
                    }
                }
            })
            .catch(error => {
                if (onFailure !== undefined) {
                    onFailure();
                }
                setLoading(false);
                console.log(error);
            });
    }

    const context: ContextModel = {
        isClientAIEnabled,
        isClientTranscriptsEnabled,
        isClient,
        isExec,
        isAdmin,
        isQA,
        isAgent,
        isRTM,
        setIsRTM,
        setIsClientAIEnabled,
        setIsClientTranscriptsEnabled,
        setIsClient,
        setIsExec,
        setIsAdmin,
        setIsQA,
        setIsAgent,
        isResqAnywhere,
        isTeamLeader,
        setIsTeamLeader,
        isQATeamLeader,
        setIsQATeamLeader,
        isDataDialler,
        setIsDataDialler,
        showMessage,
        post,
        get,
        checkRoles,
        loading,
        wavesurfer,
        waveRef
    }

    return (
        <APIContext.Provider value={context}>
            {children}
        </APIContext.Provider>
    );
}